// @ts-nocheck TODO
import picoModal from "picomodal";
import {findFirstFocusableElement} from "../../main/utils/document.js";

const PN_MODAL_CLASS = "pn-modal";

// The value is the tagName of the custom element for the modal
export const MODALS = {
	CREATE_PROJECT: "pn-create-project-modal",
	INT_ACCOUNT:    "pn-int-account-modal",
	INT_LOGIN:      "pn-int-login-modal",
	INT_SIGNUP:     "pn-int-signup-modal"
};

interface modalParams {
	modalContent: HTMLElement;
	parent:       ShadowRoot | null;

	afterClose?:       () => void;
	id?:               string;
	isBasicModal?:     boolean;
	isSingleUseModal?: boolean;
}

export class Modal {
	constructor(params: modalParams) {
		const {parent, id} = params;

		if (parent === null)
			throw new Error("ShadowRoot required as parent.");

		this.id = id;
		this.modal = null;
		this.hostElement = parent.host;

		// If there's already a modal with the same ID in the DOM, error
		if (document.getElementById(this.getHostId(id)))
			throw new Error("Modal with the same id already exists.", id);

		this.initModal(params);
	}

	getHostId(id) {
		return `pn-modal-${id}`;
	}

	initModal(params: modalParams) {
		const {
			modalContent,
			parent,

			afterClose,
			id,
			isBasicModal = true,
			isSingleUseModal = false
		} = params;

		// Add a class for all PN modals so that they can be easily identified
		this.hostElement.id = this.getHostId(id);
		this.hostElement.classList.add(PN_MODAL_CLASS);

		if (isBasicModal)
			modalContent.classList.add("basic-modal-content");

		this.modal = picoModal({
			closeHtml:  '<i class="icon-close btn-ext-icon"></i>',
			content:    modalContent,
			focus:      false, // Handle focus ourselves
			modalClass: "custom-scroll-bar-light",
			modalId:    id,
			parent:     parent,

			// Override some styles for the modal and overlay
			modalStyles: function (styles) {
				styles.borderRadius = "6px";
				styles.boxSizing = "content-box";
				styles.padding = "0";
				styles.zIndex = 2147483667;
			},
			overlayStyles: function (styles) {
				styles.zIndex = 2147483645;
				styles.opacity = 0.8;
			}
		});

		if (this.modal === null)
			throw Error("Error initiating modal");

		// Remove picomodal's inline width styling
		const modalElem = this.modal.modalElem();
		modalElem.style.removeProperty("width");

		// Remove picomodal's close button styling, add our own class
		const closeElem = this.modal.closeElem();
		closeElem.removeAttribute("style");
		closeElem.classList.add("btn-ext-icon");

		// Add animations to modal opening
		this.modal.afterShow((modal) => {
			modal.modalElem().classList.add("showing");

			this.focusFirstElement();
		});

		// In case this is a single use modal and is dismissed via the backdrop,
		// destroy so that another instance will be able to be created properly
		this.modal.afterClose(() => {
			if (isSingleUseModal)
				this.destroy();

			if (afterClose)
				afterClose();
		});

		// Don't close the modal if there's an open tooltip and esc was pressed
		let escForTooltip = false;
		modalElem.addEventListener("keydown", (event) => {
			if ((event.key === "Escape" || event.key === "Esc") && document.querySelector("[data-tippy-root].sticky"))
				escForTooltip = true;
		});

		this.modal.beforeClose((modal, event) => {
			if (escForTooltip) {
				escForTooltip = false; // reset the flag
				event.preventDefault(); // stop the modal from closing
			}
		});
	}

	show() {
		// Close any other open modals
		const selector = `.${PN_MODAL_CLASS}:not([id="${this.getHostId(this.id)}"])`;
		document.querySelectorAll(selector).forEach(modalElem => {
			modalElem.modal.hide();
		});

		// Show this modal
		this.modal?.show();
	}

	hide() {
		this.modal?.close();
	}

	destroy() {
		this.modal?.destroy();
		this.modal = null;

		this.hostElement.remove();
	}

	focusFirstElement() {
		const elem = findFirstFocusableElement(this.modal.modalElem());

		// Set a short delay before focusing the element, or the element doesn't
		// get focused
		setTimeout(() => elem.focus(), 0);
	}

	/**
	 * Opens a different modal after closing the current modal.
	 */
	openNewModal(modalName) {
		this.hide();

		attachModal(modalName);
	}
};

/**
 * Attach a modal to the DOM. Modals are currently shown immediately.
 * @param modalName
 */
export const attachModal = (modalName) => {
	const newModal = document.createElement(modalName);
	document.body.append(newModal);
};
